import {useEffect, useState} from 'react';
import {noteAuthFailure} from './session';

// Stored uploads are only served to signed-in callers, but an <img> tag cannot
// send an Authorization header.
//
// The first attempt fetched the bytes with a token and handed them to the page as
// a blob: URL. That works, and it is not cacheable at all. A blob URL is never
// written to the browser's HTTP cache, it was revoked on unmount, and every post
// row fetched its own copy - so a gallery re-downloaded every photograph on each
// visit, and the browser's network panel filled with repeats of the same file.
//
// So the credential goes in the URL instead. The server signs the path with a
// short expiry and serves that signed URL normally, which lets the browser cache,
// reuse and lazily load it the way it would any other image.
//
// Remote URLs and data URIs are passed through untouched, so records still
// pointing at the old external host keep working.

const API_ORIGIN = process.env.REACT_APP_SERVER || '';

export function isStoredFile(path) {
    return typeof path === 'string' && path.startsWith('/api/files/');
}

export function absoluteFileUrl(path) {
    if (!isStoredFile(path)) return path;
    return `${API_ORIGIN}${path}`;
}

// '/api/files/photos/a.jpg' -> 'photos/a.jpg'
function toStoredPath(url) {
    return String(url).replace(/^\/api\/files\//, '');
}

// Signatures last an hour on the server. Asking well before that expires means a
// page left open does not start failing; re-signing early is free because the
// signature is only exchanged for a URL, not for the file.
const RENEW_BEFORE_MS = 5 * 60 * 1000;

const signedCache = new Map();

// Cached for the life of the tab so a photograph appearing in several places is
// signed once, and every caller ends up with the identical URL - which is what
// lets the browser hit its cache instead of re-fetching.
export function getSignedUrl(url) {
    const entry = signedCache.get(url);

    if (entry && entry.expiresAt * 1000 - Date.now() > RENEW_BEFORE_MS) {
        return entry.url;
    }

    return null;
}

async function requestSignedUrl(url, signal) {
    const response = await fetch(
        `${API_ORIGIN}/api/files-sign?path=${encodeURIComponent(toStoredPath(url))}`,
        {
            headers: {Authorization: `Bearer ${localStorage.getItem('token')}`},
            signal,
        }
    );

    // A 401 here means the session ended, not that this one file is unavailable.
    // Ending the session is the same for a file request as for anything else.
    if (noteAuthFailure(response) || !response.ok) {
        throw new Error(`Could not authorise file (${response.status})`);
    }

    const {expiresAt, signature} = await response.json();
    const signed = `${absoluteFileUrl(url)}?exp=${expiresAt}&sig=${encodeURIComponent(signature)}`;

    signedCache.set(url, {url: signed, expiresAt});

    return signed;
}

// Resolves a stored path to a signed, cacheable URL. Returns the input untouched
// for anything this application does not host.
export default function useSignedFileUrl(path) {
    const [signedUrl, setSignedUrl] = useState(() => {
        if (!isStoredFile(path)) return path;
        return getSignedUrl(path);
    });
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        if (!isStoredFile(path)) {
            setSignedUrl(path);
            return undefined;
        }

        const cached = getSignedUrl(path);
        if (cached) {
            setSignedUrl(cached);
            return undefined;
        }

        const controller = new AbortController();
        setFailed(false);

        requestSignedUrl(path, controller.signal)
            .then(setSignedUrl)
            .catch(e => {
                // An aborted request is the component unmounting or moving on.
                if (e.name !== 'AbortError') setFailed(true);
            });

        return () => controller.abort();
    }, [path]);

    return {url: signedUrl, failed, loading: isStoredFile(path) && !signedUrl && !failed};
}

// Downloads a stored file. The browser's own download machinery cannot carry the
// token, so this fetches it and hands it over as a blob. This is a deliberate
// one-off: a download is not something worth caching.
export async function downloadAuthenticatedFile(path, filename) {
    const response = await fetch(absoluteFileUrl(path), {
        headers: {Authorization: `Bearer ${localStorage.getItem('token')}`},
    });

    if (noteAuthFailure(response) || !response.ok) {
        throw new Error(`Could not download file (${response.status})`);
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}